export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'

function getBaseUrl(): string {
  // 1. Check for explicit environment variable first
  try {
    const meta: any = (import.meta as any)
    if (typeof meta !== 'undefined' && meta?.env?.VITE_API_URL) {
      return meta.env.VITE_API_URL as string
    }
  } catch {}
  
  // 2. Check for window override (for testing)
  if (typeof window !== 'undefined') {
    const anyWindow = window as any
    if (anyWindow.__API_BASE_URL__) {
      return anyWindow.__API_BASE_URL__
    }
    
    // 3. Auto-detect environment based on hostname
    const hostname = window.location?.hostname;
    console.log('🔍 API Client: Detecting hostname:', hostname);
    if (hostname && hostname.includes('dateshake-test')) {
      console.log('🔍 API Client: Using test environment');
      return 'https://dateshake-test.up.railway.app';
    } else if (hostname && (hostname.includes('date-shake.com') || hostname.includes('www.date-shake.com'))) {
      console.log('🔍 API Client: Using production environment');
      return 'https://dateshake-production.up.railway.app';
    } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
      console.log('🔍 API Client: Using local development');
      return 'http://localhost:3001'; // Local development
    }
  }
  
  // 4. Fallback for local development
  return 'http://localhost:3001'
}

function getAuthHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('ds_token') : null
  return token ? { 'Authorization': `Bearer ${token}` } : {}
}

export async function apiFetch<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  const base = getBaseUrl()
  const url = `${base}${path}`
  console.log('🔍 API Fetch:', { base, path, url, headers: getAuthHeaders() })
  
  const res = await fetch(url, {
    headers: { 
      'Content-Type': 'application/json', 
      ...getAuthHeaders(),
      ...(options.headers || {}) 
    },
    ...options
  })
  
  console.log('🔍 API Response:', { status: res.status, statusText: res.statusText, url })
  
  const raw = await res.text()
  console.log('🔍 API Raw Response:', raw.substring(0, 200) + (raw.length > 200 ? '...' : ''))
  
  const data = raw ? (() => { try { return JSON.parse(raw) } catch { return raw } })() : null
  if (!res.ok) {
    const message = (data as any)?.error || `${res.status} ${res.statusText}`
    console.error('❌ API Error:', message)
    throw new Error(message)
  }
  return data as T
}

export const Api = {
  // Auth endpoints
  me: () => apiFetch('/api/auth/me'),
  login: (body: any) => apiFetch('/api/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  register: (body: any) => apiFetch('/api/auth/register', { method: 'POST', body: JSON.stringify(body) }),
  
  // Preferences
  preferences: {
    get: () => apiFetch('/api/preferences'),
    save: (body: any) => apiFetch('/api/preferences', { method: 'PUT', body: JSON.stringify(body) }),
  },
  
  // Plans
  plans: {
    list: () => apiFetch('/api/plans'),
    create: (body: any) => apiFetch('/api/plans', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: any) => apiFetch(`/api/plans/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    delete: (id: string) => apiFetch(`/api/plans/${id}`, { method: 'DELETE' }),
    setDefault: (id: string) => apiFetch(`/api/plans/${id}/default`, { method: 'PUT' })
  },
  
  // Partners
  partners: {
    list: () => apiFetch('/api/partners'),
    create: (body: any) => apiFetch('/api/partners', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: any) => apiFetch(`/api/partners/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    delete: (id: string) => apiFetch(`/api/partners/${id}`, { method: 'DELETE' })
  },
  
  // Ideas
  ideas: {
    feelingLucky: (body?: any) => apiFetch('/api/ideas/feeling-lucky', { method: 'POST', body: JSON.stringify(body || {}) }),
    feelingLuckyGuest: (body?: any) => apiFetch('/api/ideas/feeling-lucky-guest', { method: 'POST', body: JSON.stringify(body || {}) })
  },
  
  // Entertainment
  entertainment: {
    search: (query: string, city?: string) => apiFetch(`/api/entertainment/search/${encodeURIComponent(query)}?city=${city || ''}`)
  }
}

// Admin API client
export const apiClient = {
  get: (path: string) => apiFetch(path, { method: 'GET' }),
  post: (path: string, data?: any) => apiFetch(path, { 
    method: 'POST', 
    body: data ? JSON.stringify(data) : undefined 
  }),
  put: (path: string, data?: any) => apiFetch(path, { 
    method: 'PUT', 
    body: data ? JSON.stringify(data) : undefined 
  }),
  delete: (path: string) => apiFetch(path, { method: 'DELETE' })
}


